import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import { Provider } from "react-redux";
import reportWebVitals from './reportWebVitals';
import { BrowserRouter } from 'react-router-dom';
import { configureStore } from '@reduxjs/toolkit';
import rootReducer from './core/store';
import ErrorBoundary from './core/components/common/ErrorBoundary';
import logger from './core/utils/logger';
import { initSentry } from './core/utils/sentry';
import './i18n';

// Initialize Sentry early (before app renders)
// Only works if REACT_APP_SENTRY_DSN is set in .env
initSentry();

/**
 * Validate required environment variables
 */
const validateEnvironment = (): void => {
  const required = [
    'REACT_APP_DEFAULTAUTH',
    'REACT_APP_API_URL',
  ];

  const missing = required.filter(key => !process.env[key]);

  if (missing.length > 0) {
    logger.error('Missing required environment variables', { missing });

    // Show user-friendly error
    const errorMessage = `
      <div style="
        display: flex;
        align-items: center;
        justify-center;
        min-height: 100vh;
        font-family: system-ui, -apple-system, sans-serif;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        padding: 20px;
      ">
        <div style="
          max-width: 500px;
          background: white;
          color: #1a202c;
          padding: 40px;
          border-radius: 12px;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
        ">
          <h1 style="margin: 0 0 16px; font-size: 24px; color: #e53e3e;">
            ⚠️ Configuration Error
          </h1>
          <p style="margin: 0 0 16px; line-height: 1.6;">
            Missing required environment variables:
          </p>
          <ul style="margin: 0 0 24px; padding-left: 20px;">
            ${missing.map(key => `<li style="margin: 4px 0;"><code style="background: #f7fafc; padding: 2px 8px; border-radius: 4px;">${key}</code></li>`).join('')}
          </ul>
          <p style="margin: 0; font-size: 14px; color: #718096;">
            Please check your <code style="background: #f7fafc; padding: 2px 8px; border-radius: 4px;">.env</code> file and ensure all required variables are set.
          </p>
        </div>
      </div>
    `;

    document.body.innerHTML = errorMessage;
    throw new Error(`Missing environment variables: ${missing.join(', ')}`);
  }

  logger.info('Environment validated successfully', {
    nodeEnv: process.env.NODE_ENV,
    apiUrl: process.env.REACT_APP_API_URL,
  });
};

// Validate environment before initializing app
try {
  validateEnvironment();
} catch (error) {
  logger.error('Environment validation failed', error);
  // Error already displayed to user, stop execution
  throw error;
}

// Configure Redux store
const store = configureStore({
  reducer: rootReducer,
  devTools: process.env.NODE_ENV !== 'production', // Disable DevTools in production
});

// Initialize app
const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <Provider store={store}>
        <BrowserRouter basename={process.env.PUBLIC_URL}>
          <App />
        </BrowserRouter>
      </Provider>
    </ErrorBoundary>
  </React.StrictMode>
);

// Performance monitoring
// In production, you should send these to your analytics service
reportWebVitals((metric) => {
  if (process.env.NODE_ENV === 'production') {
    // Example: Send to analytics
    // analytics.track('WebVitals', metric);
    logger.debug('Web Vitals', metric);
  }
});

// Log app initialization
logger.info('Basiq360 Application initialized', {
  version: '1.0.0',
  environment: process.env.NODE_ENV,
  timestamp: new Date().toISOString(),
});
